import { maybe, rd } from '@passionware/monads';
import { injectConfig } from '@passionware/platform-react';
import { createMatcher } from '@passionware/query-toolkit';
import { differenceBy } from 'lodash';
import has from 'lodash/has';
import React, { Fragment, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Label } from 'v1/components/shared';
import './TagInput.scss';
import { CollectionField } from '../../../../../../v5/patterns/widgets/CollectionField';
import { ComboboxCreateItemQuery } from '../../../../../../v5/design-system/Combobox';

type Tag = {
  id: string | number;
  tag?: string;
  name?: string;
};

type Suggestion = {
  id: string | number;
  tag?: string;
  name?: string;
};

type TagInputProps = {
  className?: string;
  inputClassname?: string;
  label?: string;
  placeholder?: string;
  tags?: Tag[];
  suggestions?: Suggestion[];
  onChange: (tags: Tag[]) => void;
  isReadOnly?: boolean;
  creatable?: boolean;
  sortAlphabetically?: boolean;
  disableAdd?: boolean;
  autocomplete?: boolean;
  createButtonOnNewLine?: boolean;
  addLabel?: ReactNode;
  addIcon?: ReactNode;
  renderTag?: (tag: Tag) => ReactNode;
};

const TagInput: React.FC<TagInputProps> = ({
  label = 'Tags',
  tags: _tags,
  suggestions: _suggestions,
  onChange,
  disableAdd,
  autocomplete,
  creatable = false,
  sortAlphabetically,
  createButtonOnNewLine = true,
  addLabel,
  addIcon,
  renderTag,
  ...props
}) => {
  const finalCreatable =
    creatable &&
    maybe.mapOrElse(disableAdd, disableAdd => disableAdd !== true, true);

  const tags = _tags || [];
  const suggestions = _suggestions || [];
  const formattedTags = tags.map(t => ({
    id: t.id ? t.id.toString() : `temp-${uuidv4()}`,
    content: renderTag?.(t),
    text:
      (has(t, 'tag') ? t.tag : '') ||
      (has(t, 'name') ? t.name : '') ||
      (typeof t === 'string' ? t : '')
  }));
  const formattedSuggestions = (suggestions || [])
    .map(t => ({
      id: t.id ? t.id.toString() : `temp-${uuidv4()}`,
      content: renderTag?.(t),
      text:
        (has(t, 'tag') ? t.tag : '') ||
        (has(t, 'name') ? t.name : '') ||
        (typeof t === 'string' ? t : '')
    }))
    .sort((a, b) => {
      return sortAlphabetically
        ? a.text.localeCompare(b.text, 'en', { sensitivity: 'base' })
        : 0;
    })
    .filter(({ id }) => tags.findIndex(tag => +tag.id === +id) === -1);

  const tagsAndSuggestions = [...formattedTags, ...formattedSuggestions];
  return (
    <Fragment>
      {label && <Label>{label}</Label>}
      <SomeCollectionField
        mode={props.isReadOnly ? 'display' : 'edit'} // readOnly={props.isReadOnly}
        data={formattedTags.map(x => x.id)} //tags={formattedTags}
        tagsAndSuggestions={tagsAndSuggestions}
        suggestions={differenceBy(formattedSuggestions, formattedTags, value =>
          String(value.text)
        )}
        onRemove={(tag, index) => {
          onChange(tags.filter((_, i) => index !== i));
        }}
        onAdd={({ id, text }) => {
          //handleAddition
          const parsedId = isNaN(parseInt(id)) ? id : parseInt(id);
          const tag = parsedId
            ? suggestions.find(t => t.id === parsedId)
            : suggestions.find(t => t.tag === text);

          if (tag) {
            onChange([...tags, tag]);
          } else if (
            finalCreatable ||
            (typeof id === 'string' && id.includes('temp'))
          ) {
            onChange([...tags, { tag: text } as Tag]); // todo here we don't pass id which looks mandatory
          } else {
            console.warn('Tag not found, creation is disabled');
          }
        }}
        onMove={() => {
          // ???
        }}
        placeholder={props.placeholder || 'Enter tag'}
        draggable={false} //allowDragDrop={false}
        createButtonOnNewLine={createButtonOnNewLine}
        addLabel={addLabel}
        addIcon={addIcon}
        allowCreate={finalCreatable}
      />
    </Fragment>
  );
};

export default TagInput;

type TagOption = {
  id: string;
  text: string;
  content?: ReactNode;
};

const SomeCollectionField = injectConfig(CollectionField<TagOption>)
  .fromProps<{ suggestions: TagOption[]; tagsAndSuggestions: TagOption[] }>(
    api => ({
      // promise-like function to resolve id to the item
      useResolveById: () => {
        const props = api.useProps();

        return (id: string) =>
          maybe.getOrThrow(
            Promise.resolve(
              maybe.getOrThrow(
                props.tagsAndSuggestions.find(x => x.id === id),
                `Option with id ${id} not found`
              )
            )
          );
      },
      useOptions: query => {
        const props = api.useProps();
        return rd.of(
          props.suggestions
            .filter(createMatcher(query, x => x.text))
            .map(x => ({ id: x.id, label: x.text, content: x.content }))
        );
      },
      promptRenderer: data => (
        <Trans
          ns="v1_shared"
          i18nKey="TagInput.add"
          context={data.length ? 'many' : undefined}
        />
      ),
      createContent: (
        <>
          Create: <ComboboxCreateItemQuery />
        </>
      ),
      useCreatePromise: () => query =>
        Promise.resolve({ id: 'new', text: query }),
      getId: x => x.id,
      rowRenderer: x => x.content ?? <div>{x.text}</div>,
      useGetGhostItem: () => query => ({ id: 'ghost', text: query }),
      useSelectedItems: keys => {
        const props = api.useProps();
        return rd.of(
          keys.map(key => props.tagsAndSuggestions.find(x => x.id === key)!)
        );
      }
    })
  )
  .transformProps(x => x.skipFields('suggestions', 'tagsAndSuggestions'));
